import React from "react";
import { motion } from "framer-motion";
import { Clock, Tag } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import { SERVICES, formatKr } from "@/data/content";
import { WhatsAppButton } from "./WhatsAppButton";
import { SectionHeading } from "./SectionHeading";

const reveal = {
  hidden: { opacity: 0, y: 14 },
  show: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut", delay: i * 0.05 },
  }),
};

function ServiceCard({ service, index, compact }) {
  const { t, lang } = useLanguage();
  const s = service[lang];
  return (
    <motion.article
      custom={index}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      data-testid={`service-card-${service.key}`}
      className="group flex flex-col rounded-2xl bg-[rgba(20,21,27,0.72)] border border-[rgba(201,169,110,0.18)] backdrop-blur-sm p-6 hover:border-[rgba(201,169,110,0.4)] hover:-translate-y-0.5 transition-[transform,border-color] duration-300"
    >
      <h3 className="font-display text-xl md:text-2xl text-foreground">{s.name}</h3>
      <p className="mt-3 text-sm text-muted-foreground leading-relaxed flex-1">{s.desc}</p>

      <div className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-foreground/70">
        <span className="inline-flex items-center gap-1.5" data-testid={`service-${service.key}-price`}>
          <Tag className="h-3.5 w-3.5 text-[#C9A96E]" />
          {service.fromPrice ? `${t.services.fromLabel} ${formatKr(service.fromPrice)}` : t.services.priceLabel}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5 text-[#C9A96E]" />
          {t.services.durationLabel}
        </span>
      </div>

      <WhatsAppButton
        serviceKey={service.key}
        source="service-card"
        size="default"
        className="mt-6 w-full"
        testId={`service-${service.key}-whatsapp-cta`}
      >
        {t.cta.service}
      </WhatsAppButton>
    </motion.article>
  );
}

export function Services() {
  const { t, lang } = useLanguage();
  const standalone = SERVICES.filter((s) => !s.cluster);
  const cluster = SERVICES.filter((s) => s.cluster);

  return (
    <section id="services" className="py-16 sm:py-20 lg:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <SectionHeading
          overline={t.services.overline}
          title={t.services.title}
          subtitle={t.services.subtitle}
        />

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
          {standalone.map((s, i) => (
            <ServiceCard key={s.key} service={s} index={i} />
          ))}

          {/* Brows & lashes cluster (services 3 + 4 visually paired) */}
          <div
            data-testid="services-brows-lashes-cluster"
            className="md:col-span-2 rounded-2xl border border-[rgba(201,169,110,0.22)] bg-[rgba(15,16,20,0.5)] p-5 sm:p-6"
          >
            <div className="flex items-baseline justify-between flex-wrap gap-2 mb-5">
              <h3 className="font-display text-2xl text-foreground">
                {t.services.clusterTitle}
              </h3>
              <p className="text-sm text-muted-foreground">{t.services.clusterSubtitle}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              {cluster.map((s, i) => (
                <ServiceCard key={s.key} service={s} index={i} compact />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
